"use client";

import { Label } from "radix-ui";
import { type ComponentProps, type ReactNode, forwardRef, useId } from "react";
import { type FieldValues, type RegisterOptions, useFormContext } from "react-hook-form";
import { cn } from "../../..";

interface RadioProps extends ComponentProps<"input"> {
	label: string | ReactNode;
	variant?: "default" | "button";
	registerOptions?: RegisterOptions<FieldValues>;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function RadioRef(props, ref) {
	const { label, className, variant = "default", registerOptions, ...remainingProps } = props;
	const id = useId();

	const methods = useFormContext();
	const registeredProps =
		methods && props.name
			? methods.register(props.name, { shouldUnregister: true, ...registerOptions })
			: { ref };

	return (
		<div
			className={cn("flex items-center", className, {
				"relative px-3 py-2": variant === "button",
			})}
		>
			<input
				id={id}
				type="radio"
				{...registeredProps}
				{...remainingProps}
				className={cn(
					"form-radio cursor-pointer border-shades text-primary-medium focus:outline-none motion-safe:transition-colors",
					{
						"hover:border-primary-medium focus-visible:border-primary-medium focus-visible:ring-primary-medium":
							variant === "default",
						"peer z-1 focus-visible:ring-0 focus-visible:ring-offset-0": variant === "button",
					},
				)}
			/>

			{variant === "button" && (
				<Label.Root
					htmlFor={id}
					className="absolute inset-0 cursor-pointer rounded border border-primary bg-shades-light peer-checked:bg-primary-light peer-focus-visible:border-primary-medium"
				/>
			)}

			<Label.Root
				htmlFor={id}
				className={cn("pl-2 leading-tight", { "z-1": variant === "button" })}
			>
				{label}
				{props.required && "*"}
			</Label.Root>
		</div>
	);
});
